import { useEffect, useState } from "react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { Megaphone, FileText, Newspaper, Mail, Calendar, Building } from "lucide-react";
import api from "@/lib/api";

function Counter({ to, duration = 1.8, suffix = "" }) {
    const target = Number(to) || 0;
    const mv = useMotionValue(0);
    const rounded = useTransform(mv, (v) => Math.round(v));
    const [display, setDisplay] = useState(target); // safe fallback: show value immediately

    useEffect(() => {
        const unsub = rounded.on("change", (v) => setDisplay(v));
        return () => unsub();
    }, [rounded]);

    useEffect(() => {
        // Start the count-up animation as soon as a real (non-zero) value arrives.
        // We don't depend on IntersectionObserver – on mobile it sometimes never
        // fires for elements already inside the viewport, leaving the value at 0.
        if (target <= 0) {
            mv.set(0);
            setDisplay(0);
            return;
        }
        mv.set(0);
        const controls = animate(mv, target, { duration, ease: "easeOut" });
        return controls.stop;
    }, [target, duration, mv]);

    return <span>{display}{suffix}</span>;
}

const ITEMS_FALLBACK = [
    { key: "announcements", label: "Komunikatów", Icon: Megaphone, color: "from-amber-400 to-amber-600" },
    { key: "news", label: "Wiadomości", Icon: Newspaper, color: "from-emerald-400 to-emerald-600" },
    { key: "resolutions", label: "Uchwał", Icon: FileText, color: "from-blue-400 to-blue-600" },
    { key: "subscribers", label: "Subskrybentów newslettera", Icon: Mail, color: "from-rose-400 to-rose-600" },
    { key: "years", label: "Lat wspólnoty", Icon: Calendar, color: "from-purple-400 to-purple-600" },
    { key: "buildings", label: "Budynki C/D/E/F", Icon: Building, color: "from-teal-400 to-teal-600" },
];

export const Stats = () => {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/stats").then((r) => setStats(r.data || {})).catch(() => setStats({}));
    }, []);

    if (!stats) return null;

    const items = [
        { ...ITEMS_FALLBACK[0], value: stats.announcements || 0 },
        { ...ITEMS_FALLBACK[1], value: stats.news || 0 },
        { ...ITEMS_FALLBACK[2], value: stats.resolutions || 0 },
        { ...ITEMS_FALLBACK[3], value: stats.subscribers || 0 },
        { ...ITEMS_FALLBACK[4], value: stats.years || 0, label: stats.years_label || ITEMS_FALLBACK[4].label },
        { ...ITEMS_FALLBACK[5], value: stats.buildings || 0, label: stats.buildings_label || ITEMS_FALLBACK[5].label },
    ];

    return (
        <section data-testid="stats-section" className="py-20 md:py-24 bg-white relative overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D4A537]/40 to-transparent" />
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12 max-w-2xl mx-auto"
                >
                    <p className="text-[#D4A537] uppercase tracking-[0.25em] text-xs font-semibold mb-4">Wspólnota w liczbach</p>
                    <h2 className="font-serif text-3xl md:text-4xl text-[#092E22] leading-tight">
                        Społeczność Dąbrówki Wiślanej w jednym spojrzeniu
                    </h2>
                </motion.div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
                    {items.map((it, idx) => {
                        const Icon = it.Icon;
                        return (
                            <motion.div
                                key={it.key}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: idx * 0.08 }}
                                data-testid={`stat-${it.key}`}
                                className="group relative bg-[#FAF8F3] rounded-2xl p-5 md:p-6 border border-gray-100 hover:border-[#D4A537]/40 hover:-translate-y-1 transition-all duration-300"
                            >
                                <div className={`inline-flex w-11 h-11 rounded-xl bg-gradient-to-br ${it.color} text-white items-center justify-center mb-3 shadow-sm`}>
                                    <Icon size={20} />
                                </div>
                                <div className="font-serif text-3xl md:text-4xl text-[#092E22] leading-none mb-1">
                                    <Counter to={it.value} />
                                </div>
                                <p className="text-xs text-gray-600 leading-tight">{it.label}</p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default Stats;
